<template>
  <div class="base-el-select" :class="finalClass">
    <div ref="prepend" v-if="$slots.prepend" class="el-input-group__prepend" :class="prependFinalClass" @click="onPrependClick"><slot name="prepend" /></div>
    <el-select ref="ElSelect" class="w-full" :class="innerClass" v-model="localValue" :filter-method="doFilter" :popper-append-to-body="popperAppendToBody" :multiple="multiple" :disabled="isDisabled" 
      :collapse-tags="collapseTags" :value-key="valueKey" :size="size" :clearable="clearable" :multiple-limit="multipleLimit" :name="name" :autocomplete="autocomplete"
      :placeholder="finalPlaceholder" :filterable="filterable" :allow-create="allowCreate" :loading="loading" :loadingText="finalLoadingText" :no-match-text="finalNoMatchText" 
      :no-data-text="finalNoDataText" :popper-class="finalPopperClass" :reserve-keyword="reserveKeyword" :default-first-option="defaultFirstOption" :automatic-dropdown="automaticDropdown"
      @change="onChange" @visible-change="onVisibleChange" @remove-tag="onRemoveTag" @clear="onClear" @blur="onBlur" @focus="onFocus" >
      <template v-if="$slots.prefix" #prefix>
        <slot name="prefix" />
      </template>
      <span v-if="hasGroups" class="groups" :set="tempGroups = discard(defaults, finalGroups)">
        <el-option-group v-for="(group, key) in tempGroups" :key="key" :label="key">
          <el-option v-for="option in group" :key="optionKey(option)" :label="optionLabel(option)" :value="optionValue(option)" :disabled="optionDisabled(option)" />
        </el-option-group>
        <el-option-group v-if="hasDefaultGroups" :set="group = Object.values(preserve(defaults, finalGroups)).flat()" key="default" label="">
          <el-option v-for="option in group" :key="optionKey(option)" :label="optionLabel(option)" :value="optionValue(option)" :disabled="optionDisabled(option)" />
        </el-option-group>
      </span>
      <span v-else class="options">
        <el-option v-for="option in finalOptions" :key="optionKey(option)" :label="optionLabel(option)" :value="optionValue(option)" :disabled="optionDisabled(option)" />
      </span>
      <template v-if="$slots.empty" #empty>
        <slot name="empty" />
      </template>
    </el-select>
    <span v-if="finalArrowOptions.icon" class="custom-arrow-icon" @mouseenter="arrowhover = true" @mouseleave="arrowhover = false" >
      <span class="arrow-wrap">
        <base-icon class="arrow-icon" :class="{ 'is-reverse': isopen, hidden: localValue && clearable && ($refs.ElSelect.inputHovering || arrowhover) }" :name="finalArrowOptions.icon" :size="finalArrowOptions.size" :custom="finalArrowOptions.custom" />
      </span>
    </span>
    <div v-if="$slots.append" class="el-input-group__append" :class="appendFinalClass" @click="onAppendClick"><slot name="append" /></div>
  </div>
</template>

<script>
import { i18n } from '@/i18n'
import getNestedValue from '@/services/functions/getNestedValue'
import setNestedValue from '@/services/functions/setNestedValue'
import { isObjectLike } from 'lodash'

class PrimitiveOptionWrapper {
  value
  /**
   * @param {string|number|boolean|symbol} value
   * @param {string} path
   */
  constructor (value, path) {
    this.value = value
    if (path && path !== 'value' && path !== '.') {
      setNestedValue(path, this, value)
    }
  }
}

export default {
  name: 'base-el-select',
  props: {
    // #region VModel
    value: {
      type: [String, Number, Boolean, Array, Object],
      required: true,
      validator: (value) => ['string', 'number', 'boolean', 'object'].includes(typeof value) || value === null,
    },
    // #endregion
    
    // #region ElSelect Properties
    multiple: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    collapseTags: {
      type: Boolean,
      default: false,
    },
    valueKey: {
      type: String,
      default: '.'
    },
    size: {
      type: String,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    multipleLimit: {
      type: Number,
      default: 0,
    },
    name: {
      type: String,
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    placeholder: {
      type: String,
      default: ''
    },
    filterable: {
      type: Boolean,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
    filterMethod: {
      type: Function,
    },
    remote: {
      type: Boolean,
      default: false,
    },
    remoteMethod: {
      type: Function,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingText: {
      type: String,
      default: ''
    },
    noMatchText: {
      type: String,
      default: ''
    },
    noDataText: {
      type: String,
      default: ''
    },
    popperClass: {
      type: String,
      default: '',
    },
    reserveKeyword: {
      type: Boolean,
      default: false,
    },
    defaultFirstOption: {
      type: Boolean,
      default: false,
    },
    popperAppendToBody: {
      type: Boolean,
      default: true,
    },
    automaticDropdown: {
      type: Boolean,
      default: false,
    },
    // #endregion
    
    // #region BaseElSelect Properties
    options: {
      type: Array,
      default: () => [],
    },
    groups: {
      type: Object,
      default: () => ({}),
    },
    queryReplacer: {
      type: Function,
      default: function (query) {
        return query 
      }
    },
    customQuery: {
      type: Function
    },
    optionKey: {
      type: Function,
      default: function (option) {
        return this.defOptionKey(option)
      }
    },
    optionLabel: {
      type: Function,
      default: function (option) {
        return this.defOptionLabel(option)
      }
    },
    optionValue: {
      type: Function,
      default: function (option) {
        return this.defOptionValue(option)
      }
    },
    optionDisabled: {
      type: Function,
      default: function (option) {
        return this.defOptionDisabled(option)
      }
    },
    createMethod: {
      type: Function,
      default: value => value
    },
    caseSensitive: {
      type: Boolean,
      default: false,
    },
    arrowIcon: {
      type: String,
      default: '',
    },
    arrowSize: {
      type: String,
      default: '',
    },
    arrowCustom: {
      type: String,
      default: '',
    },
    fixArrow: {
      type: Boolean,
      default: false,
    },
    arrowOptions: {
      type: Object,
      default: () => ({}),
    },
    prependDisabled: {
      type: Boolean,
      default: false,
    },
    appendDisabled: {
      type: Boolean,
      default: false,
    },
    innerClass: {
      type: String,
      default: ''
    }
    // #endregion
  },
  data () {
    return {
      falsys: [undefined, null, ''],
      defaults: ['default', undefined, null, ''],
      localValue: '',
      isopen: false,
      arrowhover: false,
      innerDisabled: false,
      observer: null,
    }
  },
  mounted () {
    if (this.$refs?.ElSelect?.$refs?.reference?.$el && !this.finalArrowOptions.icon) {
      const elinput = this.$refs.ElSelect.$refs.reference.$el
      const icon = elinput.querySelector('.el-input__suffix-inner .el-input__icon:first-child')
      this.applyClasses()
      // Creamos un nuevo observador de mutaciones
      this.observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          // Verificamos si la mutación afectó al icono
          if (mutation.target.classList.contains('el-input__icon')) {
            // Aplicamos las clases nuevamente después de la mutación
            this.applyClasses()
          }
        })
      })

      // Observamos cambios en el nodo raíz del componente
      this.observer.observe(icon, { attributes: true, childList: true, subtree: true })
    }
  },
  watch: {
    options: {
      deep: true,
      handler: function (value) {
        this.doFilter(this.$refs?.ElSelect?.query ?? '')
      }
    },
    groups: {
      deep: true,
      handler: function (value) {
        this.doFilter(this.$refs?.ElSelect?.query ?? '')
      }
    },
    value: {
      immediate: true,
      handler: function (value, oldValue) {
        if (value !== oldValue) {
          this.localValue = value
        }
      }
    },
    localValue: {
      immediate: true,
      handler: function (value, oldValue) {
        if (value !== oldValue) {
          if (value && this.allowCreate && this.$refs.ElSelect?.options) {
            const option = this.$refs.ElSelect.options.find(opt => opt.created && opt.currentLabel === value)
            if (option) {
              if (this.options[0]?.created) {
                this.options.shift()
              }
              let newoption = this.createMethod(value)
              if (!isObjectLike(newoption)) {
                const primitiveValue = newoption
                newoption = new PrimitiveOptionWrapper(primitiveValue)
              }
              this.$set(newoption, 'created', true)
              this.options.unshift(newoption)
              this.$nextTick(() => {
                let newvalue = null
                if (newoption instanceof PrimitiveOptionWrapper) {
                  newvalue = newoption.value
                } else {
                  newvalue = this.optionValue(newoption)
                }
                this.localValue = newvalue
                this.$emit('input', newvalue)
              })
              return
            }
          }
          this.$emit('input', value)
        }
      }
    },
    'finalArrowOptions.size': function (value, oldValue) {
      if (value !== oldValue) {
        this.applyClasses()
      }
    },
    'finalArrowOptions.custom': function (value, oldValue) {
      if (value !== oldValue) {
        this.applyClasses()
      }
    },
  },
  computed: {
    isDisabled: function () {
      return !!(this.disabled || this.innerDisabled)
    },
    hasGroups: function () {
      return Object.keys(this.groups || {}).some(key => !this.isFalsy(key) && String(key).toLowerCase() !== 'default') || 
        this.options.some(x => !this.isFalsy(x?.groupName) && String(x?.groupName).toLowerCase() !== 'default')
    },
    hasDefaultGroups: function () {
      return this.hasGroups && Object.keys(this.finalGroups).map(x => String(x).toLowerCase()).some(x => this.defaults.includes(x))
    },
    finalGroups: function () {
      const groups = {}
      if (this.hasGroups) {
        Object.assign(groups, this.groups)
        for (const item of this.options) {
          const groupName = item.groupName || 'default'
          if (!groups[groupName]) {
            this.$set(groups, groupName, [])
          }
          groups[groupName].push(item)
        }
      }
      return Object.assign({}, groups)
    },
    finalOptions: function () {
      const options = []
      if (!this.hasGroups) {
        Object.assign(options, this.options)
        for (const item of Object.values(this.groups)) {
          options.push(...item)
        }
      }
      return Object.assign([], options)
    },
    finalPopperClass: function () {
      return `base-el-select-dopdown base-el-select-popper ${this.popperClass}`
    },
    finalArrowOptions: function () {
      const options = {
        icon: (this.arrowOptions?.icon ?? this.arrowIcon) || '', /* chevronup */
        size: (this.arrowOptions?.size ?? this.arrowSize) || '',
        custom: (this.arrowOptions?.custom ?? this.arrowCustom) || '',
        fixed: !!(Object.hasOwnProperty.call(this.arrowOptions || {}, 'fixed')? this.arrowOptions.fixed : this.fixArrow) || false,
      }
      if (options.icon && !options.size) {
        options.size = 'md'
      }
      return Object.assign({}, options)
    },
    iconSize: function () {
      return {
        'custom-size-2': this.finalArrowOptions.size === 'tiny',
        'custom-size-3': this.finalArrowOptions.size === 'xs',
        'custom-size-4': this.finalArrowOptions.size === 'xsm',
        'custom-size-5': this.finalArrowOptions.size === 'sm',
        'custom-size-6': this.finalArrowOptions.size === 'md',
        'custom-size-8': this.finalArrowOptions.size === 'lg',
        '': !['tiny', 'xs', 'xsm', 'sm', 'md', 'lg'].includes(this.finalArrowOptions.size),
      }
    },
    finalClass: function () {
      return { 
        'el-input-group': !!(this.$slots.prepend || this.$slots.append), 
        'el-input-group--prepend': !!this.$slots.prepend, 
        'el-input-group--append': !!this.$slots.append,
        'custom-arrow': !!this.finalArrowOptions.icon,
        'fixed-arrow': !!this.finalArrowOptions.fixed,
        disabled: !!this.isDisabled,
        // ...this.iconSize
      }
    },
    isPrependClickable: function () {
      return !!this.$listeners['prepend-click']
    },
    isAppendClickable: function () {
      return !!this.$listeners['append-click']
    },
    prependFinalClass: function () {
      return {
        disabled: !!this.prependDisabled, 
        clickable: this.isPrependClickable 
      }
    },
    appendFinalClass: function () {
      return {
        disabled: !!this.appendDisabled, 
        clickable: this.isAppendClickable 
      }
    },
    finalPlaceholder: function () {
      return this.placeholder || i18n.t('app.select')
    },
    finalLoadingText: function () {
      return this.loadingText || i18n.t('app.loading')
    },
    finalNoMatchText: function () {
      return this.noMatchText || i18n.t('app.no_match_text')
    },
    finalNoDataText: function () {
      return this.noDataText || i18n.t('app.no_data_text')
    },
    flatOptions: function () {
      if (this.hasGroups) {
        return Object.values(this.finalGroups).flat()
      }
      return this.finalOptions
    }
  },
  methods: {
    doFilter: function (query) {
      const myfunc = str => (this.caseSensitive ? str : str?.toLowerCase()) ?? ''
      const finalquery = this.queryReplacer(query)
      const localquery = myfunc(finalquery)
      const select = this.$refs.ElSelect
      const useCustom = typeof this.customQuery === 'function'
      select.cachedOptions.forEach((opt, i) => this.$set(opt, 'visible', (this.allowCreate && opt.created) || (!useCustom ? myfunc(opt.label).includes(localquery) : this.customQuery(this.flatOptions[i], localquery, myfunc))))
      select.filteredOptionsCount = select.cachedOptions.filter(opt => opt.visible).length
      if ((query !== finalquery || (select?.query !== finalquery && this.isopen)) && select?.$refs?.reference?.$refs?.input) {
        this.setQuery(finalquery)
      }
      this.recolocatePopper()
    },
    setQuery: function (query) {
      if (this.$refs?.ElSelect) {
        const select = this.$refs.ElSelect
        if (select?.query !== query && select?.$refs?.reference?.$refs?.input) {
          const input = select.$refs.reference.$refs.input
          const position = Math.max(Math.min(input.selectionEnd, input.selectionStart), 0)
          input.value = query
          input.selectionStart = position
          input.selectionEnd = position
          input.selectionDirection = 'forward'
          input.dispatchEvent(new Event('input', { bubbles: true }))
          input.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }
    },
    recolocatePopper: function () {
      if (this.$refs?.ElSelect) {
        if (this.$el?.offsetWidth) {
          const width = this.$el?.offsetWidth
          this.$nextTick(() => {
            const select = this.$refs.ElSelect
            if (select?.$refs?.popper?.$el) {
              const popper = select.$refs.popper.$el
              // const left = -(this.$refs.prepend?.getBoundingClientRect()?.width ?? 0)
              const left = this.$el.getBoundingClientRect().left

              // popper.querySelector('.popper__arrow').style.left = '35px'
              popper.style.setProperty('--base-width', `${width}px`)
              popper.style.setProperty('--base-left', `${left}px`)
            }
          })
        }
      }
    },
    applyClasses () {
      const elinput = this.$refs?.ElSelect?.$refs?.reference?.$el
      const arrow = elinput?.querySelector('.el-input__suffix-inner .el-input__icon:first-child')
      if (arrow) {
        const classList = [...this.getClassList(this.iconSize), ...this.getClassList(this.finalArrowOptions.custom)]
        if (classList?.length && classList.some(eclass => !arrow.classList.contains(eclass))) {
          arrow.classList.add(...classList.filter(eclass => !arrow.classList.contains(eclass)))
        }
      }
    },
    getClassList: function (src) {
      return (typeof src === 'object'? Object.entries(src).filter(x => x[1]).reduce((a, b) => `${a} ${b[0]}`, '') : String(src)).split(' ').filter(x => x)
    },
    defOptionKey: function (option) {
      const key = this.getDefOptionKey(option)
      if (key !== undefined) {
        return key
      }
      const value = this.getDefOptionValue(option)
      if (value !== undefined) {
        if (isObjectLike(value) && this.valueKey) {
          return this.getNestedValue(this.valueKey, value)
        }
        return value
      }
      const label = this.getDefOptionLabel(option)
      if (label !== undefined) {
        return label
      }
      if (option === null || option === undefined) {
        return `${option}`
      }
      return option
    },
    defOptionLabel: function (option) {
      const label = this.getDefOptionLabel(option)
      if (label !== undefined) {
        return label
      }
      const value = this.getDefOptionValue(option)
      if (value !== undefined) {
        if (isObjectLike(value) && this.valueKey) {
          return this.getNestedValue(this.valueKey, value)
        }
        return value
      }
      const key = this.getDefOptionKey(option)
      if (key !== undefined) {
        return key
      }
      if (option === null || option === undefined) {
        return ''
      }
      return option
    },
    defOptionValue: function (option) {
      const groupId = this.getDefGroupIdValue(option)
      if (groupId !== undefined) {
        return groupId
      }
  
      const value = this.getDefOptionValue(option)
      if (value !== undefined) {
        return value
      }
      const key = this.getDefOptionKey(option)
      if (key !== undefined) {
        return key
      }
      const label = this.getDefOptionLabel(option)
      if (label !== undefined) {
        return label
      }
      if (option === null || option === undefined) {
        return this.multiple ? `${option}` : option
      }
      return option
    },
    defOptionDisabled: function (option) {
      const disabled = this.getDefOptionDisabled(option)
      if (disabled !== undefined) {
        return !!disabled
      }
      return false
    },
    getDefOptionValue: function (option) {
      if (option instanceof PrimitiveOptionWrapper) {
        return option.value
      }
      if (typeof this.optionValue === 'function' && this.optionValue.name !== 'default') {
        return this.optionValue(option)
      }
      if (option !== null && option !== undefined) {
        if (!this.falsys.includes(option.id)) {
          return option.id
        }
        if (!this.falsys.includes(option.value)) {
          return option.value
        }
      }
      return undefined
    },
    getDefOptionDisabled: function (option) {
      if (typeof this.optionDisabled === 'function' && this.optionDisabled.name !== 'default') {
        return this.optionDisabled(option)
      }
      if (option !== null && option !== undefined) {
        if (!this.isFalsy(option.disabled)) {
          return !!option.disabled
        }
      }
      return undefined
    },
    getDefGroupIdValue: function (option) {
      if (typeof this.optionValue === 'function' && this.optionValue.name !== 'default') {
        return this.optionValue(option)
      }
      if (option !== null && option !== undefined) {
        if (!this.falsys.includes(option.groupId)) {
          return option.groupId
        }
      }
      return undefined
    },
    getDefOptionLabel: function (option) {
      if (typeof this.optionLabel === 'function' && this.optionLabel.name !== 'default') {
        return this.optionLabel(option)
      }
      if (option !== null && option !== undefined) {
        if (!this.isFalsy(option.label)) {
          return option.label
        }
        if (!this.isFalsy(option.name)) {
          return option.name
        }
        if (!this.isFalsy(option.Name)) {
          return option.Name
        }
        if (!this.isFalsy(option.Nombre)) {
          return option.Nombre
        }
      }
      return undefined
    },
    getDefOptionKey: function (option) {
      if (typeof this.optionKey === 'function' && this.optionKey.name !== 'default') {
        return this.optionKey(option)
      }
      if (option !== null && option !== undefined) {
        if (!this.isFalsy(option.key)) {
          return option.key
        }
        if (!this.isFalsy(option.id)) {
          return option.id
        }
        if (!this.isFalsy(option.name)) {
          return option.name
        }
      }
      return undefined
    },
    getNestedValue: function (fieldName, dataItem) {
      if (fieldName === '.') {
        return dataItem
      }
      return getNestedValue(fieldName, dataItem)
    },
    isFalsy (value) {
      return this.falsys.includes(value)
    },
    onPrependClick: function () {
      if (this.isPrependClickable && !this.prependDisabled) { 
        this.$emit('prepend-click')
      }
    },
    onAppendClick: function () {
      if (this.isAppendClickable && !this.appendDisabled) { 
        this.$emit('append-click')
      }
    },
    onChange: function (value) {
      this.doFilter('')
      this.$emit('change', value)
    },
    onVisibleChange: function (value) {
      this.isopen = value
      this.doFilter('')
      if (value) {
        this.recolocatePopper()
      }
      this.$emit('visible-change', value)
    },
    onRemoveTag: function (tag) {
      this.$emit('remove-tag', tag)
    },
    onClear: function () {
      this.doFilter('')
      if (this.options[0]?.created) {
        this.options.shift()
      }
      this.$emit('clear')
    },
    onBlur: function (event) {
      this.$emit('blur', event)
    },
    onFocus: function (event) {
      this.$emit('focus', event)
    },
    open: function () {
      if (this.$refs.ElSelect) {
        this.$refs.ElSelect.visible = true
      }
    },
    close: function () {
      if (this.$refs.ElSelect) {
        this.$refs.ElSelect.visible = true
      }
    },
    focus: function () {
      if (this.$refs.ElSelect) {
        this.$refs.ElSelect.focus()
      }
    },
    blur: function () {
      if (this.$refs.ElSelect) {
        this.$refs.ElSelect.blur()
      }
    },
    selectAll: async function () {
      if (this.multiple) {
        this.innerDisabled = true
        this.$emit('select-all', true)
        const options = this.hasGroups ? Object.values(this.finalGroups).flat() : this.finalOptions
        for (let index = 0; index < options.length; index++) {
          setTimeout(() => {
            const optvalue = this.optionValue(options[index])
            if (!this.value.includes(optvalue)) {
              this.localValue.push(optvalue)
            }
            if (options.length === this.localValue.length) {
              this.$emit('select-all', false)
              this.$emit('input', this.localValue)
              this.innerDisabled = false
            }
          }, 100)
        }
      }
    },
    unselectAll: async function () {
      if (this.multiple) {
        this.innerDisabled = true
        this.$emit('unselect-all', true)
        const options = this.hasGroups ? Object.values(this.finalGroups).flat() : this.finalOptions
        const staticLength = options.length
        for (let index = 0; index < staticLength; index++) {
          setTimeout(() => {
            const optvalue = this.optionValue(options[index])
            if (this.value.includes(optvalue)) {
              this.localValue.splice(this.localValue.indexOf(optvalue), 1)
            }
            if (this.localValue.length === 0) {
              this.$emit('unselect-all', false)
              this.$emit('input', this.localValue)
              this.innerDisabled = false
            }
          }, 100)
        }
      }
    },
  },
  beforeDestroy () {
    // Detenemos el observador de mutaciones antes de destruir el componente
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  },
}
</script>

<style scoped>
.base-el-select.disabled,
.base-el-select.disabled::v-deep * {
  cursor: not-allowed !important;
}
.el-select::v-deep .el-input .el-input__inner {
  @apply truncate !important;
}
.el-select::v-deep .el-input .el-input__inner:placeholder-shown {
  @apply truncate !important
}
.base-el-select.custom-arrow:has(.arrow-icon:hover) .el-select::v-deep .el-input__inner {
    border-color: #c0c4cc;
}
.dark .base-el-select.custom-arrow:has(.arrow-icon:hover) .el-select::v-deep .el-input .el-input__inner {
    border-color: var(--primary-DEFAULT);
    --tw-text-opacity: 1;
    color: rgba(255, 255, 255, var(--tw-text-opacity));
}

.base-el-select.fixed-arrow .el-select::v-deep .el-input .el-input__suffix .el-select__caret.is-reverse {
  transform: rotateZ(180deg);
}
.base-el-select.custom-arrow .el-select::v-deep .el-input .el-input__suffix .el-select__caret {
  display: none;
}
.base-el-select.custom-arrow:has(.arrow-icon.hidden) .el-select::v-deep .el-input .el-input__suffix .el-select__caret {
  display: inline-block;
}
.base-el-select.custom-arrow {
  position: relative !important;
}
.base-el-select.custom-arrow .el-select::v-deep .el-input .el-input__suffix .el-select__caret {
  display: none;
}
.base-el-select.custom-arrow .custom-arrow-icon {
  position: absolute;
  height: 100% !important;
  right: 5px;
  top: 0;
  text-align: center;
  color: #c0c4cc;
  transition: all 0.3s;
  pointer-events: none;
}
.base-el-select.custom-arrow .custom-arrow-icon .arrow-wrap .arrow-icon {
  height: 100% !important;
  text-align: center;
  transition: all 0.3s;
  line-height: 40px;
  font-size: 14px;
  transition: transform 0.3s;
  transform: rotateZ(180deg);
  cursor: pointer;
}
.base-el-select.custom-arrow:not(.fixed-arrow) .custom-arrow-icon .arrow-wrap .arrow-icon.is-reverse {
    transform: rotateZ(0);
}

.dark .base-el-select.custom-arrow .custom-arrow-icon .arrow-wrap .arrow-icon {
  color: var(--primary-DEFAULT);
}

.dark .base-el-select.custom-arrow:has(.el-input:hover) .custom-arrow-icon .arrow-wrap .arrow-icon,
.dark .base-el-select.custom-arrow .custom-arrow-icon .arrow-wrap .arrow-icon:hover {
  --tw-text-opacity: 1;
  color: rgba(255, 255, 255, var(--tw-text-opacity));
}

::v-deep .icon[class^=custom-size-],
.icon {
  line-height: 40px !important;
  height: 100% !important;
}
::v-deep .custom-size-2,
.custom-size-2 {
  width: 8px !important;
  font-size: 4.67px !important;
}
::v-deep .custom-size-3,
.custom-size-3 {
  width: 12px !important;
  font-size: 7px !important;
}
::v-deep .custom-size-4,
.custom-size-4 {
  width: 16px !important;
  font-size: 1.67px !important;
}
::v-deep .custom-size-5,
.custom-size-5 {
  width: 20px !important;
  font-size: 12.67px !important;
}
::v-deep .custom-size-6,
.custom-size-6 {
  width: 24px !important;
  font-size: 14px !important;
}
::v-deep .custom-size-8,
.custom-size-8 {
  width: 32px !important;
  font-size: 18.67px !important;
}

.base-el-select .el-input-group__append,
.base-el-select .el-input-group__prepend {
  transition-property: background-color, border-color, color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

.base-el-select .el-input-group__append.clickable,
.base-el-select .el-input-group__append.clickable::v-deep *,
.base-el-select .el-input-group__prepend.clickable,
.base-el-select .el-input-group__prepend.clickable::v-deep * {
  cursor: pointer !important;
}
.base-el-select .el-input-group__append.clickable.disabled,
.base-el-select .el-input-group__append.clickable.disabled::v-deep *,
.base-el-select .el-input-group__prepend.clickable.disabled,
.base-el-select .el-input-group__prepend.clickable.disabled::v-deep * {
  cursor: not-allowed !important;
}

.base-el-select .el-input-group__append.clickable:not(.disabled):hover,
.base-el-select .el-input-group__prepend.clickable:not(.disabled):hover {
  background-color: var(--primary-100);
}
.dark .base-el-select .el-input-group__append.clickable:not(.disabled):hover,
.dark .base-el-select .el-input-group__prepend.clickable:not(.disabled):hover {
  --tw-text-opacity: 1;
  color: rgba(255, 255, 255, var(--tw-text-opacity));
  background-color: var(--primary-500);
}
</style>
<style>
@property --base-width {
  syntax: "<length-percentage>|auto";
  inherits: false;
  initial-value: auto;
}
@property --base-left {
  syntax: "<length-percentage>|auto";
  inherits: false;
  initial-value: auto;
}

.base-el-select-popper {
  min-width: var(--base-width) !important;
  max-width: var(--base-width) !important;
  left: var(--base-left) !important;
}
.base-el-select-popper .popper__arrow {
  left: 35px !important;
}
</style>
